<template>
    <div class="animated fadeIn">
        <div class="kgr-row">
            <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--top kgr-row">
                <div class="kgr-postfix__fluid">
                    <kgr-kindergarten-group-select
                        id="gallery-group-select"
                        v-model="group"
                        :kindergarten-id="kindergartenId"
                    />
                </div>
                <div
                    v-if="hasGeneralWriteAccess"
                    class="kgr-postfix__fixed"
                >
                    <a
                        class="kgr-button kgr-button--accent"
                        :href="createUrl"
                    >
                        <kgr-icon
                            icon-class="add"
                            small
                            circle
                            highlight
                            class="kgr-button__icon kgr-button__icon--left"
                        />
                        <span>{{ $t('Neues Album hinzufügen') }}</span>
                    </a>
                </div>
            </div>

            <div class="kgr-row kgr-region kgr-region--default kgr-postfix kgr-postfix--gutters kgr-postfix--bottom">
                <div class="kgr-postfix__fluid">
                    <div class="kgr-alert kgr-alert--info kgr-alert--noIcon">
                        <div class="kgr-postfix kgr-postfix--smallGutters kgr-postfix--center">
                            <div class="kgr-postfix__fixed">
                                <kgr-icon
                                    icon-class="info-circle"
                                    class="kgr-postfix__icon"
                                />
                            </div>
                            <div class="kgr-postfix__fluid">
                                {{ $t('Bitte beachten Sie, dass Fotos nur für den privaten Gebrauch gedacht sind und nicht mit Unbefugten geteilt werden dürfen.') }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-show="loading"
                class="kgr-row"
            >
                <div class="kgr-row kgr-text--centered">
                    <kgr-loading />
                </div>
            </div>

            <div
                v-show="!loading"
                class="kgr-row"
            >
                <div class="kgr-grid kgr-grid--inline kgr-grid--gutters kgr-row">
                    <div
                        v-for="album in albums"
                        :key="album.id"
                        class="app-gridCard kgr-grid__cell"
                    >
                        <article class="kgr-card kgr-backgroundColor kgr-backgroundColor--white">
                            <div
                                v-if="album.coverImage"
                                class="kgr-card__image kgr-card__image--full"
                            >
                                <img :src="getCoverImageUrl(album.coverImage)" />
                            </div>

                            <div class="kgr-card__subtitle">
                                <span v-if="album.groups.length > 0">
                                    {{ album.groups.map(g => g.name).join(', ') }}
                                </span>
                                <span v-else>
                                    {{ $t('Für alle Gruppen') }}
                                </span>
                            </div>

                            <h3 class="kgr-card__name kgr-card__name--noSeparator">
                                <template v-if="isLoading(album)">
                                    <kgr-loading inline />
                                </template>
                                <template v-else>
                                    <a :href="showUrl(album)">
                                        {{ !isOriginal(album.id) && translateTo && album.translations?.title ? album.translations.title : album.title }}
                                    </a>
                                </template>
                            </h3>

                            <div class="kgr-card__contents"></div>

                            <div class="kgr-card__footer">
                                <div class="kgr-card__footerContents kgr-postfix kgr-postfix--center kgr-postfix--smallGutters">
                                    <div
                                        v-if="!hasRoleParent"
                                        class="kgr-postfix__fixed kgr-centered kgr-centered--vertically"
                                    >
                                        <kgr-publication-icon
                                            :clearing="album.clearing"
                                        />
                                    </div>
                                    <div class="kgr-postfix__fluid kgr-centered kgr-centered--vertically">
                                        {{ getParsedDate(album.createdAt) }}
                                    </div>
                                    <div class="kgr-postfix__fixed kgr-centered kgr-centered--vertically">
                                        <kgr-icon
                                            icon-class="collections"
                                            small
                                            disabled
                                            class="kgr-col kgr-col--smallMargin"
                                        />
                                        {{ album.mediaCount }}
                                    </div>
                                    <div
                                        v-if="translateTo"
                                        class="kgr-postfix__fixed kgr-centered kgr-centered--vertically"
                                    >
                                        <kgr-icon
                                            icon-class="globe"
                                            :title="isOriginal(album.id) ? $t('Übersetzung anzeigen') : $t('Originaltext anzeigen')"
                                            small
                                            hover-accent
                                            @click="toggleOriginal(album.id)"
                                        />
                                    </div>
                                    <div
                                        v-if="album.hasWriteAccess"
                                        class="kgr-postfix__fixed kgr-centered kgr-centered--vertically"
                                    >
                                        <a
                                            :href="editUrl(album)"
                                            :title="$t('Gallery.editAlbum')"
                                        >
                                            <kgr-icon
                                                icon-class="edit"
                                                hover-accent
                                                small
                                            />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </article>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { format, parseISO } from '@legacy/js/date/date-fns.js';
import GalleryRepository from '../../../js/repository/GalleryRepository.js';
import Icon from '../../../components/common/icon/Icon.vue';
import KindergartenGroupSelect from '../../../components/common/form/select/KindergartenGroupSelect.vue';
import Loading from '../../../components/common/Loading.vue';
import PublicationIcon from '../../../components/common/icon/PublicationIcon.vue';
import { getLanguageFromCookie } from '../../../js/service/LanguageHelper.js';

export default {
    components: {
        'kgr-icon': Icon,
        'kgr-loading': Loading,
        'kgr-publication-icon': PublicationIcon,
        'kgr-kindergarten-group-select': KindergartenGroupSelect,
    },
    props: {
        kindergartenId: {
            type: Number,
            required: true,
        },
        hasGeneralWriteAccess: {
            type: Boolean,
            required: false,
            default: false,
        },
        hasRoleParent: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    data() {
        return {
            error: new Error(),
            loading: true,
            albums: [],
            group: null,
            translateTo: null,
            translatingIds: [],
            showOriginalIds: [],
        };
    },
    computed: {
        createUrl() {
            return '/backend/gallery/albums/new';
        },
        untranslatedAlbums() {
            if (this.translateTo === null) {
                return [];
            }
            return this.albums.filter((album) => {
                const hasTitle = album.title !== null && album.title.trim() !== '';
                const hasDescription = album.description !== null && album.description.trim() !== '';

                const missingTitle = hasTitle && !album.translations?.title;
                const missingDescription = hasDescription && !album.translations?.description;

                return missingTitle || missingDescription;
            });
        },
    },
    watch: {
        group() {
            this.translate();
        },
    },
    created() {
        const savedLang = getLanguageFromCookie();
        if (savedLang && savedLang !== 'de_DE') {
            this.translateTo = savedLang;
        } else {
            this.load();
        }
    },
    methods: {
        showUrl(album) {
            return `/backend/gallery/album/${album.id}`;
        },
        editUrl(album) {
            return `/backend/gallery/album/${album.id}/edit`;
        },
        getCoverImageUrl(coverImage) {
            if (!coverImage) {
                return '';
            }

            if (coverImage.type === 'image') {
                return `/backend/thumbnail/image/${coverImage.id}/card_full/${coverImage.hash}`;
            }

            if (coverImage.type === 'video') {
                return `/backend/thumbnail/video/${coverImage.id}/card_full/${coverImage.fileName}`;
            }

            return '';
        },
        getParsedDate(date) {
            const parsedDate = parseISO(date);
            const formatted = format(parsedDate, 'dd.MM.yyyy');

            return formatted;
        },
        toggleOriginal(albumId) {
            const index = this.showOriginalIds.indexOf(albumId);
            if (index === -1) {
                this.showOriginalIds.push(albumId);
            } else {
                this.showOriginalIds.splice(index, 1);
            }
        },
        isOriginal(albumId) {
            return this.showOriginalIds.includes(albumId);
        },
        async translate() {
            await this.load();
            const missing = this.untranslatedAlbums.map((a) => a.id);
            if (missing.length === 0) {
                return;
            }

            this.translatingIds = [...missing];

            try {
                await GalleryRepository.createTranslations(missing, this.translateTo);

                await Promise.all(
                    missing.map(async (id) => {
                        const updatedItem = await GalleryRepository.find(this.kindergartenId, id, { lang: this.translateTo });
                        const index = this.albums.findIndex((a) => a.id === id);
                        if (index !== -1) {
                            this.albums[index] = updatedItem;
                        }
                        this.translatingIds = this.translatingIds.filter((x) => x !== id);
                    }),
                );
            } catch {
                this.$notify.error('Beim Übersetzen ist ein Fehler aufgetreten.');
                this.translatingIds = [];
            }
        },
        isLoading(album) {
            return this.translatingIds.indexOf(album.id) !== -1;
        },
        async load() {
            this.loading = true;
            return GalleryRepository.findAll(this.kindergartenId, { lang: this.translateTo, group: this.group?.id })
                .then((response) => {
                    this.albums = response.data;
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style scoped>
.app-gridCard {
    width: 350px;
}

.kgr-card__image img {
    width: 100%;
    height: auto;
    object-fit: cover;
}
</style>
